<template>
  <div
    class="selectable-card"
    :class="{ active: selected, disabled: disabled }"
    @click="handleClick"
  >
    <div class="selectable-card__icon" v-if="icon">
      <CIcon v-if="isCoreIcon" :name="icon" size="lg" />
      <fa-icon v-else :icon="icon" />
    </div>
    <div class="selectable-card__content">
      <div class="selectable-card__header">
        <div class="selectable-card__label">{{ label }}</div>
        <div v-if="help" class="selectable-card__help-anchor">
          <button
            ref="helpBtn"
            class="selectable-card__help-btn"
            :class="{ 'is-open': showHelp }"
            @click.stop="toggleHelp"
            title="Ver explicação"
          >
            <CIcon name="cil-info" size="sm" />
          </button>
        </div>
      </div>
      <div class="selectable-card__description" v-if="description">
        {{ description }}
      </div>
    </div>
    <div class="selectable-card__check" :class="{ checked: selected }">
      <CIcon v-if="selected" name="cil-check" size="sm" />
    </div>
  </div>
</template>

<script>
export default {
  name: "SelectableCard",
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: "",
    },
    help: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    selected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showHelp: false,
    };
  },
  computed: {
    isCoreIcon() {
      return this.icon && this.icon.startsWith("cil-");
    },
  },
  methods: {
    handleClick() {
      if (!this.disabled) {
        this.$emit("select", this.value);
      }
    },
    toggleHelp() {
      if (this.showHelp) {
        this.destroyPopover();
      } else {
        this.createPopover();
      }
    },
    createPopover() {
      this.showHelp = true;
      const btn = this.$refs.helpBtn;
      if (!btn) return;

      const rect = btn.getBoundingClientRect();
      const popoverWidth = 300;
      let left = rect.right - popoverWidth;
      if (left < 8) left = 8;
      if (left + popoverWidth > window.innerWidth - 8) {
        left = window.innerWidth - popoverWidth - 8;
      }

      const el = document.createElement("div");
      el.style.cssText =
        "position:fixed;" +
        "top:" + (rect.bottom + 8) + "px;" +
        "left:" + left + "px;" +
        "width:" + popoverWidth + "px;" +
        "background:#fff;" +
        "border:1px solid #dddce9;" +
        "border-radius:8px;" +
        "box-shadow:0 4px 16px rgba(0,0,0,.12);" +
        "z-index:99999;" +
        "padding:12px 14px;" +
        "font-family:inherit;" +
        "animation:scPopoverIn .15s ease;";

      const body = document.createElement("div");
      body.style.cssText = "font-size:12px;color:#444;line-height:1.6;padding-right:16px;";
      body.textContent = this.help;

      const close = document.createElement("button");
      close.style.cssText =
        "position:absolute;top:6px;right:8px;" +
        "background:none;border:none;font-size:16px;" +
        "color:#999;cursor:pointer;padding:0;line-height:1;";
      close.innerHTML = "&times;";
      close.onmouseenter = () => (close.style.color = "#333");
      close.onmouseleave = () => (close.style.color = "#999");
      close.onclick = (e) => { e.stopPropagation(); this.destroyPopover(); };

      el.appendChild(body);
      el.appendChild(close);
      el.onclick = (e) => e.stopPropagation();

      if (!document.getElementById("sc-popover-keyframes")) {
        const s = document.createElement("style");
        s.id = "sc-popover-keyframes";
        s.textContent =
          "@keyframes scPopoverIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}";
        document.head.appendChild(s);
      }

      document.body.appendChild(el);
      this._popoverEl = el;

      setTimeout(() => {
        document.addEventListener("click", this._onOutside);
      }, 0);

      this._scrollParent = this.findScrollParent(this.$el);
      if (this._scrollParent) {
        this._scrollParent.addEventListener("scroll", this._onScroll);
      }
    },
    destroyPopover() {
      this.showHelp = false;
      if (this._popoverEl && this._popoverEl.parentNode) {
        this._popoverEl.parentNode.removeChild(this._popoverEl);
      }
      this._popoverEl = null;
      document.removeEventListener("click", this._onOutside);
      if (this._scrollParent) {
        this._scrollParent.removeEventListener("scroll", this._onScroll);
        this._scrollParent = null;
      }
    },
    findScrollParent(el) {
      let p = el.parentElement;
      while (p) {
        const s = getComputedStyle(p);
        if (s.overflowY === "auto" || s.overflowY === "scroll") return p;
        p = p.parentElement;
      }
      return null;
    },
  },
  created() {
    this._popoverEl = null;
    this._scrollParent = null;
    this._onOutside = (e) => {
      const btn = this.$refs.helpBtn;
      if (
        this._popoverEl &&
        !this._popoverEl.contains(e.target) &&
        btn &&
        !btn.contains(e.target)
      ) {
        this.destroyPopover();
      }
    };
    this._onScroll = () => {
      this.destroyPopover();
    };
  },
  beforeDestroy() {
    this.destroyPopover();
  },
};
</script>

<style scoped>
.selectable-card {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;
}

.selectable-card:hover:not(.disabled) {
  border-color: #c5c8cc;
  background-color: #fafafa;
}

.selectable-card.active {
  border-color: #3a3e81;
  background-color: #eceaf3;
}

.selectable-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.selectable-card__icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background-color: #f2f2f8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #3a3e81;
}

.selectable-card.active .selectable-card__icon {
  background-color: #dddce9;
}

.selectable-card__content {
  flex: 1;
  min-width: 0;
}

.selectable-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.selectable-card__label {
  font-size: 13px;
  font-weight: 600;
  color: #2c2c2c;
  line-height: 1.3;
}

.selectable-card__help-btn {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: #aaa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.selectable-card__help-anchor {
  display: inline-flex;
}

.selectable-card__help-btn:hover,
.selectable-card__help-btn.is-open {
  color: #3a3e81;
  background-color: rgba(58, 62, 129, 0.1);
}

.selectable-card__description {
  font-size: 11px;
  color: #777;
  margin-top: 2px;
  line-height: 1.3;
}

.selectable-card__check {
  width: 18px;
  height: 18px;
  border: 2px solid #ccc;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: center;
  transition: all 0.15s ease;
}

.selectable-card__check.checked {
  background-color: #3a3e81;
  border-color: #3a3e81;
  color: #fff;
}
</style>
